import React from 'react';
import { Box, Typography } from '@mui/material';
import { BookOpen } from 'lucide-react';

const navy = '#0d1b2a';
const gold = '#C9A227';
const goldLight = '#e6c45e';
const ivory = '#fcfaf5';
const navyText = '#1e3a5f';
const grayText = '#374151';
const font = 'Frank Ruhl Libre, David, serif';

const CategoryRow = ({ label }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 0.75,
    bgcolor: 'rgba(30,58,95,0.06)',
    borderRight: `3px solid ${gold}`,
    px: 1.5, py: 0.5, mt: 1, mb: 0.25,
  }}>
    <Typography sx={{ fontFamily: font, fontSize: '0.6rem', fontWeight: 700, color: navyText, letterSpacing: '0.05em' }}>
      {label}
    </Typography>
  </Box>
);

const LimudRow = ({ labelHe, valueHe, url, even }) => (
  <Box sx={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    bgcolor: even ? '#faf6ef' : 'white',
    borderBottom: '1px solid #e8e0d0',
    px: 1.5, py: 0.6,
  }}>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
      <Typography sx={{ fontFamily: font, fontSize: '0.68rem', color: navyText, fontWeight: 700 }}>
        {labelHe}
      </Typography>
      {url && <BookOpen size={9} style={{ color: gold, opacity: 0.4, flexShrink: 0, marginTop: 2 }} />}
    </Box>
    <Box sx={{ flex: 1, mx: 1, borderBottom: '1px dotted #d4c9b0', minWidth: 20, alignSelf: 'center', mb: '3px' }} />
    <Typography sx={{ fontFamily: font, fontSize: '0.66rem', color: grayText, fontWeight: 500 }}>
      {valueHe}
    </Typography>
  </Box>
);

/**
 * Ledger — "פנקס הלומדים"
 * טבלה רשמית, header navy, שורות מתחלפות עם leader dots.
 */
const Ledger = ({ today, items, groups, loading, error, title, showTitle }) => {
  const hebrewDate = today?.hebrewDate?.rendered || '';
  const displayGroups = groups || [];
  const flatItems = !groups && Array.isArray(items) ? items : null;

  let rowIndex = 0;

  return (
    <Box sx={{
      width: '100%', height: '100%', overflow: 'hidden',
      bgcolor: ivory, fontFamily: font, direction: 'rtl',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header bar */}
      {showTitle && (
        <Box sx={{ bgcolor: navy, px: 2, py: 1.25, textAlign: 'center' }}>
          <Typography sx={{
            fontFamily: font, fontSize: '0.82rem', fontWeight: 700, color: ivory,
            letterSpacing: '0.1em',
          }}>
            ✦ {title} ✦
          </Typography>
          {hebrewDate && (
            <Typography sx={{ fontFamily: font, fontSize: '0.58rem', color: goldLight, mt: 0.25 }}>
              {hebrewDate}
            </Typography>
          )}
        </Box>
      )}

      {!showTitle && hebrewDate && (
        <Box sx={{ bgcolor: navy, px: 2, py: 0.75, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: font, fontSize: '0.6rem', color: goldLight }}>
            {hebrewDate}
          </Typography>
        </Box>
      )}

      {/* Table content */}
      <Box sx={{ flex: 1, overflow: 'auto',
        '&::-webkit-scrollbar': { width: '2px' },
        '&::-webkit-scrollbar-thumb': { background: '#d4c9b0', borderRadius: '1px' },
      }}>
        {displayGroups.map((group) => (
          <Box key={group.category}>
            {displayGroups.length > 1 && <CategoryRow label={group.label} />}
            {group.items.map((item) => {
              const even = rowIndex++ % 2 === 0;
              return <LimudRow key={item.key} {...item} even={even} />;
            })}
          </Box>
        ))}

        {flatItems && flatItems.map((item, i) => (
          <LimudRow key={item.key || i} {...item} even={i % 2 === 0} />
        ))}
      </Box>

      {/* Footer */}
      <Box sx={{ borderTop: `1px solid rgba(201,162,39,0.3)`, px: 2, py: 0.5, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: font, fontSize: '0.5rem', color: gold, opacity: 0.5, letterSpacing: '0.08em' }}>
          תורה אור
        </Typography>
      </Box>
    </Box>
  );
};

export default Ledger;
